/* eslint-disable no-console */
import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Spinner, Alert, Button, Row, Col, ListGroup, Badge } from 'react-bootstrap';
import { client } from '../../lib/AmplifyClient';
import type { Schema } from '../../../amplify/data/resource';
import { useUserContext } from '../Context/UserContext';
import outputs from '../../../amplify_outputs.json';

type Issue = Schema['Issue']['type'];
type Profile = Schema['Profile']['type'];


const PROPOSE_STRATEGIES_URL: string = (outputs as any).custom?.proposeStrategiesApiUrl;

function unwrap<T>(maybe: any): T | null {
  if (!maybe) return null;
  if (typeof maybe === 'object' && 'data' in maybe) return (maybe.data as T) ?? null;
  return (maybe as T) ?? null;
}

function coerceStrings(arr?: (string | null)[] | null): string[] {
  return (arr ?? []).filter((s): s is string => typeof s === 'string' && s.trim().length > 0);
}

function TextBlock({ text }: { text: string }) {
  return <div style={{ whiteSpace: 'pre-wrap' }}>{text}</div>;
}

/** ---- helpers just for bestSolutionJson rendering ---- */
function toStringArray(value: any): string[] {
  if (Array.isArray(value)) return coerceStrings(value);
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return coerceStrings(parsed);
    } catch {
      /* ignore */
    }
    return value
      .split(/\r?\n|•|—|-{1,2}/g)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

/** Reads from best.normalized if present (wrapped shape saved by Solutions page) */
function BestSolutionPanel({ best }: { best: any }) {
  const src = best && typeof best === 'object' ? best.normalized ?? best : best;

  const title = (src?.title ?? src?.name ?? 'Best Solution') as string;
  const summary = (src?.summary ?? src?.description ?? '') as string;

  const steps = toStringArray(src?.steps);
  const benefits = toStringArray(src?.benefits);
  const risks = toStringArray(src?.risksOrTradeoffs ?? src?.risks ?? src?.tradeoffs);
  const successMetric = (src?.successMetric ?? src?.success ?? src?.metric ?? '') as string;

  const Section = ({ label, items }: { label: string; items: string[] }) =>
    items.length ? (
      <>
        <h6 className="mt-3 mb-2">{label}</h6>
        <ListGroup variant="flush">
          {items.map((s, i) => (
            <ListGroup.Item key={i}>{s}</ListGroup.Item>
          ))}
        </ListGroup>
      </>
    ) : null;

  return (
    <Card className="mb-3">
      <Card.Body>
        <h5 className="mb-0">Best Solution</h5>

        {title && <h6 className="mt-2">{title}</h6>}
        {summary && <Card.Text className="mb-2">{summary}</Card.Text>}

        <Section label="Steps" items={steps} />
        <Section label="Benefits" items={benefits} />
        <Section label="Risks / Trade-offs" items={risks} />

        {successMetric ? (
          <>
            <h6 className="mt-3 mb-1">Success Metric</h6>
            <TextBlock text={successMetric} />
          </>
        ) : null}
      </Card.Body>
    </Card>
  );
}

export default function ProposeStrategiesComponent() {
  const { id: rawId } = useParams();
  const id = useMemo(() => decodeURIComponent(rawId ?? ''), [rawId]);
  const navigate = useNavigate();

  const { isAuthenticated, firstName, email } = useUserContext() as {
    isAuthenticated: boolean;
    firstName: string | null;
    email: string | null;
  };

  const [issue, setIssue] = useState<Issue | null>(null);
  const [loading, setLoading] = useState<'init' | 'run' | 'idle'>('init');
  const [error, setError] = useState<string | null>(null);

  /** DCS comes from Profile.dcsMode only */
  const [initiatorDcsMode, setInitiatorDcsMode] = useState<string>('');
  const [collaboratorDcsMode, setCollaboratorDcsMode] = useState<string>('');

  /** Ensure Issue.status is "strategies" on entry */
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await (client as any).models.Issue.get({ id, authMode: 'userPool' }).catch(() => null);
        const current = unwrap<Issue>(res);
        if (!current) return;

        const beforeStatus = (current as any).status;
        if (beforeStatus !== 'strategies') {
          console.log('[Propose] Setting Issue.status to "strategies" (was:', beforeStatus, ')');
          const updated = await (client as any).models.Issue
            .update({ id, status: 'strategies' }, { authMode: 'userPool' })
            .catch(() => null);
          const afterUpdate = unwrap<Issue>(updated);
          const afterStatus = (afterUpdate as any)?.status;
          console.log('[Propose] Read-back Issue.status after update:', afterStatus);
        } else {
          console.log('[Propose] Issue.status already "strategies" — read-back OK.');
        }
      } catch (e) {
        console.warn('[Propose] Failed to set Issue.status="strategies"', e);
      }
    })();
  }, [id]);

  /** ObserveQuery Issue */
  useEffect(() => {
    if (!id) return;
    let first = true;

    console.log('[Propose] querying Issue id:', id);

    const sub = (client as any).models.Issue
      .observeQuery({
        filter: (q: any) => q.id.eq(id),
        selectionSet: [
          'id',
          'status',
          'initiatorEmail',
          'collaboratorEmail',
          'initiatorStrategies',
          'collaboratorStrategies',
          'initiatorCommunicationTips',
          'collaboratorCommunicationTips',
          'proposeStrategiesStatus',
          'proposeStrategiesError',
          'agreedIssue',
          'bestSolutionJson',
          'updatedAt',
        ],
        authMode: 'userPool',
      })
      .subscribe(
        async ({ items }: any) => {
          console.log('[Propose] observeQuery items len:', items?.length);
          const next: Issue | null = items?.[0] ?? null;

          if (!next && first) {
            try {
              const res = await (client as any).models.Issue.get({ id, authMode: 'userPool' }).catch(() => null);
              const one = unwrap<Issue>(res);
              if (one) {
                setIssue(one);
                setLoading('idle');
                first = false;
                return;
              }
            } catch {}
          }

          setIssue(next);
          console.log('[Propose] observeQuery status:', (next as any)?.status);
          setLoading('idle');
          first = false;
        },
        (err: any) => {
          console.warn('[Propose] observeQuery error:', err);
          setError(`Failed to load issue: ${String(err?.errors?.[0]?.message || err?.message || err)}`);
          setLoading('idle');
        }
      );

    return () => sub.unsubscribe?.();
  }, [id]);

  /** Subscribe to initiator Profile.dcsMode by email */
  useEffect(() => {
    const pEmail = (issue?.initiatorEmail || '').trim();
    if (!pEmail) return;

    let unsub: any;
    try {
      unsub = (client as any).models.Profile
        .observeQuery({
          filter: (q: any) => q.email.eq(pEmail),
          selectionSet: ['email', 'dcsMode', 'updatedAt'],
          authMode: 'userPool',
        })
        .subscribe(
          ({ items }: any) => {
            const p: Profile | undefined = items?.[0];
            const mode = typeof (p as any)?.dcsMode === 'string' ? ((p as any).dcsMode as string).trim() : '';
            setInitiatorDcsMode(mode);
            console.log('[Propose] initiator dcsMode:', mode);
          },
          (err: any) => {
            console.warn('[Propose] initiator Profile observeQuery error:', err);
          }
        );
    } catch (e) {
      console.warn('[Propose] initiator Profile subscribe failed:', e);
    }
    return () => unsub?.unsubscribe?.();
  }, [issue?.initiatorEmail]);

  /** Subscribe to collaborator Profile.dcsMode by email */
  useEffect(() => {
    const pEmail = (issue?.collaboratorEmail || '').trim();
    if (!pEmail) return;

    let unsub: any;
    try {
      unsub = (client as any).models.Profile
        .observeQuery({
          filter: (q: any) => q.email.eq(pEmail),
          selectionSet: ['email', 'dcsMode', 'updatedAt'],
          authMode: 'userPool',
        })
        .subscribe(
          ({ items }: any) => {
            const p: Profile | undefined = items?.[0];
            const mode = typeof (p as any)?.dcsMode === 'string' ? ((p as any).dcsMode as string).trim() : '';
            setCollaboratorDcsMode(mode);
            console.log('[Propose] collaborator dcsMode:', mode);
          },
          (err: any) => {
            console.warn('[Propose] collaborator Profile observeQuery error:', err);
          }
        );
    } catch (e) {
      console.warn('[Propose] collaborator Profile subscribe failed:', e);
    }
    return () => unsub?.unsubscribe?.();
  }, [issue?.collaboratorEmail]);

  const safe = (s?: string | null) => (typeof s === 'string' ? s.trim() : '');

  /** Identify viewer role */
  const viewerRole = useMemo<'initiator' | 'collaborator' | 'unknown'>(() => {
    const viewer = safe(email).toLowerCase();
    const iEmail = safe(issue?.initiatorEmail).toLowerCase();
    const cEmail = safe(issue?.collaboratorEmail).toLowerCase();
    if (viewer && iEmail && viewer === iEmail) return 'initiator';
    if (viewer && cEmail && viewer === cEmail) return 'collaborator';
    return 'unknown';
  }, [email, issue?.initiatorEmail, issue?.collaboratorEmail]);

  /** Server-driven generation status */
  const generation = useMemo(() => {
    const status = (issue as any)?.proposeStrategiesStatus as
      | 'idle'
      | 'in_progress'
      | 'succeeded'
      | 'failed'
      | undefined;

    const hasInitiator = initiatorDcsMode.length > 0;
    const hasCollaborator = collaboratorDcsMode.length > 0;
    const prereqsOk = Boolean(hasInitiator && hasCollaborator);

    const hasOutputs =
      (issue?.initiatorStrategies?.length || 0) > 0 ||
      (issue?.collaboratorStrategies?.length || 0) > 0 ||
      (issue?.initiatorCommunicationTips?.length || 0) > 0 ||
      (issue?.collaboratorCommunicationTips?.length || 0) > 0;

    return {
      status: status ?? 'idle',
      prereqsOk,
      hasOutputs,
    };
  }, [issue, initiatorDcsMode, collaboratorDcsMode]);

  /** Auto-run when both dcsMode values exist and nothing generated yet and not in progress */
  useEffect(() => {
    if (!id) return;
    const canAutoRun = generation.prereqsOk && !generation.hasOutputs && generation.status !== 'in_progress';
    if (!canAutoRun) return;

    (async () => {
      try {
        await runPropose();
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, generation.prereqsOk, generation.hasOutputs, generation.status]);

  async function runPropose() {
    if (!id) return;
    setError(null);
    setLoading('run');

    try {
      if (!PROPOSE_STRATEGIES_URL) throw new Error('Missing proposeStrategies API URL.');
      if (!initiatorDcsMode || !collaboratorDcsMode) {
        setLoading('idle');
        return;
      }

const url = `${PROPOSE_STRATEGIES_URL}?id=${encodeURIComponent(id)}`;
const resp = await fetch(url, { method: 'POST' });
if (!resp.ok) throw new Error(`enqueue failed: ${resp.status}`);


      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(text || `HTTP ${resp.status}`);
      }
    } catch (e: any) {
      setError(`Generation failed: ${String(e?.message || e)}`);
    } finally {
      setLoading('idle');
    }
  }

  function RenderList({ items }: { items?: (string | null)[] | null }) {
    const arr = coerceStrings(items);
    if (!arr.length) return <div className="text-muted">None yet.</div>;
    return (
      <ListGroup variant="flush">
        {arr.map((s, idx) => (
          <ListGroup.Item key={idx}>{s}</ListGroup.Item>
        ))}
      </ListGroup>
    );
  }

  /** Single-party panel */
  function PartyPanel({ party }: { party: 'initiator' | 'collaborator' }) {
    const isInitiator = party === 'initiator';
    const title = isInitiator ? 'Initiator' : 'Collaborator';
    const isYou =
      (party === 'initiator' && viewerRole === 'initiator') ||
      (party === 'collaborator' && viewerRole === 'collaborator');

    const dcsMode = isInitiator ? initiatorDcsMode : collaboratorDcsMode;
    const strategies = (isInitiator ? issue?.initiatorStrategies : issue?.collaboratorStrategies) as any;
    const tips = (isInitiator ? issue?.initiatorCommunicationTips : issue?.collaboratorCommunicationTips) as any;

    return (
      <Card>
        <Card.Body>
          <h5 className="mb-2">
            {isYou ? 'Your Plan' : title}
            {isYou && <Badge bg="secondary" className="ms-2">You</Badge>}
          </h5>
          <div className="text-muted small mb-2">DCS: {dcsMode || '—'}</div>
          <h6 className="mb-2">Recommended Strategies</h6>
          <RenderList items={strategies} />
          <hr />
          <h6>Your Communication Tips</h6>
          <RenderList items={tips} />
        </Card.Body>
      </Card>
    );
  }

  if (loading === 'init') {
    return (
      <Container fluid className="min-vh-100 py-4">
        <Row className="justify-content-center">
          <Col xs={12} sm={11} md={10} lg={9} xl={8} xxl={7}>
            <Card body className="text-center shadow-sm">
              <Spinner animation="border" role="status" className="me-2" />
              Loading…
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  if (!issue) {
    return (
      <Container fluid className="min-vh-100 py-4">
        <Row className="justify-content-center">
          <Col xs={12} sm={11} md={10} lg={9} xl={8} xxl={7}>
            <Card className="shadow-sm">
              <Card.Body>
                <Alert variant="danger">Issue not found.</Alert>
                <Button variant="secondary" onClick={() => navigate(-1)}>
                  Go back
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  const greeting = isAuthenticated
    ? `Hello${firstName ? `, ${firstName}` : email ? `, ${email}` : ''}`
    : null;

  /** bestSolutionJson pretty-print helpers */
  const safeStr = (s?: string | null) => (typeof s === 'string' ? s.trim() : '');
  const bestSolutionJsonStr = safeStr((issue as any)?.bestSolutionJson);
  let bestSolutionPretty = '';
  let bestSolutionObj: any | null = null;
  try {
    if (bestSolutionJsonStr) {
      const parsed = JSON.parse(bestSolutionJsonStr);
      bestSolutionObj = parsed && typeof parsed === 'object' ? parsed : null;
      bestSolutionPretty = JSON.stringify(parsed, null, 2);
    }
  } catch {
    bestSolutionPretty = bestSolutionJsonStr;
    bestSolutionObj = null;
  }
  const hasBestSolutionJson = Boolean(bestSolutionPretty);

  return (
    <Container fluid className="min-vh-100 py-4">
      {(generation.status === 'in_progress' || loading === 'run') && (
        <Row className="justify-content-center mb-3">
          <Col xs={12} sm={11} md={10} lg={9} xl={8} xxl={7}>
            <Alert variant="info" className="mb-0">
              <Spinner animation="border" size="sm" className="me-2" />
              Generating strategies and communication tips…
            </Alert>
          </Col>
        </Row>
      )}
      {generation.status === 'failed' && (
        <Row className="justify-content-center mb-3">
          <Col xs={12} sm={11} md={10} lg={9} xl={8} xxl={7}>
            <Alert variant="danger" className="mb-0">
              Generation failed
              {(issue as any)?.proposeStrategiesError ? `: ${(issue as any).proposeStrategiesError}` : '.'}
            </Alert>
          </Col>
        </Row>
      )}

      <Row className="justify-content-center">
        <Col xs={12} sm={11} md={10} lg={9} xl={8} xxl={7}>
          <Card className="shadow-sm">
            <Card.Body>
              <Row className="mb-3 align-items-center">
                <Col>
                  <h2 className="mb-0">Implementation Strategies</h2>
                </Col>
                <Col xs="auto" className="ms-auto text-end">
                  {greeting ? <span className="text-muted">{greeting}</span> : null}
                </Col>
              </Row>

              {error && (
                <Alert variant="danger" className="mb-3">
                  {error}
                </Alert>
              )}

              {issue.agreedIssue && (
                <Card className="mb-3">
                  <Card.Body>
                    <h5 className="mb-2">Agreed Issue</h5>
                    <TextBlock text={issue.agreedIssue} />
                  </Card.Body>
                </Card>
              )}

              {bestSolutionObj ? (
                <BestSolutionPanel best={bestSolutionObj} />
              ) : (
                <Card className="mb-3">
                  <Card.Body>
                    <h5 className="mb-2">Best Solution (bestSolutionJson)</h5>
                    {hasBestSolutionJson ? (
                      <pre
                        className="p-3 border rounded bg-light"
                        style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 400, overflow: 'auto' }}
                      >
                        {bestSolutionPretty}
                      </pre>
                    ) : (
                      <div className="text-muted fst-italic">No bestSolutionJson found on this Issue record.</div>
                    )}
                  </Card.Body>
                </Card>
              )}

              {viewerRole === 'initiator' && <PartyPanel party="initiator" />}
              {viewerRole === 'collaborator' && <PartyPanel party="collaborator" />}
              {viewerRole === 'unknown' && (
                <>
                  <Alert variant="warning" className="mb-3">
                    We couldn’t match your login to this session’s emails. Showing both sides in read-only mode.
                  </Alert>
                  <Row>
                    <Col md={6} className="mb-3">
                      <PartyPanel party="initiator" />
                    </Col>
                    <Col md={6} className="mb-3">
                      <PartyPanel party="collaborator" />
                    </Col>
                  </Row>
                </>
              )}

              <div className="d-flex gap-2 mt-3 justify-content-center">
                <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                  Back
                </Button>
                <Button
                  variant="success"
                  onClick={() => navigate(`/session/${encodeURIComponent(issue.id)}/implement`)}
                  disabled={!generation.hasOutputs}
                >
                  Continue to Implementation
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
